import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  ArrowUpRight,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { CLINIC, WHATSAPP_URL } from "../lib/constants";
import { useT } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contacto = () => {
  const { t } = useT();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service) {
      toast.error(t("contacto.form.errRequired"));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        message: `${t("contacto.form.msgPrefix")}${form.service}`,
      });
      toast.success(t("contacto.form.success"));
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail || t("contacto.form.errDefault");
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const hours = t("contacto.hours");
  const serviceOptions = t("contacto.serviceOptions");

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="relative py-24 md:py-40 bg-[#EEF2E9]"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-6">
        {/* ═══ CTA heading ═══ */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mb-20 md:mb-28"
        >
          <div className="flex items-center gap-3 mb-7">
            <span className="h-px w-10 bg-[#A3BD98]" />
            <span className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#ADBDAD]">
              {t("contacto.eyebrow")}
            </span>
          </div>
          <h2 className="font-serif text-[34px] md:text-[48px] lg:text-[60px] leading-[1.12] tracking-tight text-[#2A322C] font-light">
            {t("contacto.titleBefore")}
            <span className="font-semibold text-[#A3BD98]">
              {t("contacto.titleWord")}
            </span>
            {t("contacto.titleAfter")}
          </h2>
          <p className="mt-8 text-[17px] md:text-lg text-[#5C6660] leading-[1.8] max-w-xl">
            {t("contacto.lead")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
          {/* ═══ LEFT — Contact info ═══ */}
          <div className="lg:col-span-5 space-y-12">
            <div className="space-y-8">
              <InfoRow
                icon={<MapPin size={18} strokeWidth={1.5} />}
                label={t("contacto.labels.morada")}
                value={CLINIC.addressLine}
                testid="contact-address"
              />
              <InfoRow
                icon={<Phone size={18} strokeWidth={1.5} />}
                label={t("contacto.labels.telefone")}
                value={CLINIC.phoneDisplay}
                href={`tel:${CLINIC.phoneRaw}`}
                testid="contact-phone"
              />
              <InfoRow
                icon={<Mail size={18} strokeWidth={1.5} />}
                label={t("contacto.labels.email")}
                value={CLINIC.email}
                href={`mailto:${CLINIC.email}`}
                testid="contact-email"
              />
              <div data-testid="contact-hours" className="flex items-start gap-5">
                <div className="shrink-0 w-12 h-12 rounded-full bg-[#A3BD98]/15 flex items-center justify-center text-[#2A322C] mt-1">
                  <Clock size={18} strokeWidth={1.75} />
                </div>
                <div className="flex-1">
                  <div className="text-[10.5px] tracking-[0.28em] uppercase font-semibold text-[#5C6660] mb-2.5">
                    {t("contacto.labels.horario")}
                  </div>
                  <ul className="space-y-1.5 text-[#2A322C]">
                    {hours.map((h) => (
                      <li
                        key={h.day}
                        className="flex gap-6 text-[14.5px] md:text-[15px]"
                      >
                        <span className="w-40 text-[#5C6660]">{h.day}</span>
                        <span>{h.time}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-btn"
              className="group inline-flex items-center gap-2 text-[#A3BD98] text-sm font-medium border-b border-[#A3BD98] pb-1 hover:text-[#2A322C] transition-colors"
            >
              {t("contacto.whatsappPrompt")}
              <ArrowUpRight
                size={16}
                className="group-hover:rotate-45 transition-transform"
              />
            </a>

            {/* Map */}
            <div
              className="mt-6 md:mt-10 aspect-[4/3] rounded-[12px] overflow-hidden shadow-[0_14px_40px_-20px_rgba(42,50,44,0.18)]"
              data-testid="contact-map"
            >
              <iframe
                title={t("contacto.mapTitle")}
                src={CLINIC.mapsEmbed}
                className="w-full h-full grayscale-[30%] contrast-[.95]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* ═══ RIGHT — Form ═══ */}
          <motion.form
            onSubmit={submit}
            data-testid="contact-form"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6 lg:col-start-7 bg-white rounded-[16px] p-8 md:p-12 lg:p-14 shadow-[0_22px_60px_-30px_rgba(42,50,44,0.18)] text-center lg:text-left"
          >
            <h3 className="font-serif text-[28px] md:text-[34px] text-[#2A322C] font-semibold mb-3 tracking-tight">
              {t("contacto.formTitle")}
            </h3>
            <p className="text-[15px] text-[#5C6660] leading-relaxed">
              {t("contacto.formSubtitle")}
            </p>

            <div
              aria-hidden
              className="my-10 h-px bg-gradient-to-r from-transparent via-[#2A322C]/10 to-transparent"
            />

            <div className="space-y-7">
              <Field
                label={t("contacto.form.name")}
                testid="form-name"
                value={form.name}
                onChange={(v) => update("name", v)}
                placeholder={t("contacto.form.namePH")}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field
                  label={t("contacto.form.email")}
                  type="email"
                  testid="form-email"
                  value={form.email}
                  onChange={(v) => update("email", v)}
                  placeholder={t("contacto.form.emailPH")}
                />
                <Field
                  label={t("contacto.form.phone")}
                  testid="form-phone"
                  value={form.phone}
                  onChange={(v) => update("phone", v)}
                  placeholder={t("contacto.form.phonePH")}
                />
              </div>

              <div>
                <label className="text-[11px] tracking-[0.26em] uppercase font-semibold text-[#ADBDAD] block mb-2.5">
                  {t("contacto.form.service")}
                </label>
                <Select
                  value={form.service}
                  onValueChange={(v) => update("service", v)}
                >
                  <SelectTrigger
                    data-testid="form-service"
                    className="w-full bg-[#FEFEFE] border border-[#E4E9DE] rounded-[10px] h-[56px] px-4 text-[15px] text-[#2A322C] focus:ring-0 focus:ring-offset-0 focus:border-[#A3BD98] focus:shadow-[0_0_0_4px_rgba(163,189,152,0.15)] hover:border-[#A3BD98]/60 transition-all duration-200"
                  >
                    <SelectValue placeholder={t("contacto.form.servicePH")} />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceOptions.map((s) => (
                      <SelectItem
                        key={s}
                        value={s}
                        data-testid={`form-service-option-${s}`}
                      >
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-btn"
                className="group w-full inline-flex items-center justify-center gap-2 bg-[#EE8984] text-white rounded-[10px] px-8 py-[22px] text-[15px] font-semibold tracking-wide mt-4 hover:bg-[#CF8376] hover:scale-[1.02] hover:shadow-[0_16px_40px_-12px_rgba(238,137,132,0.55)] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? t("contacto.form.submitting") : t("contacto.form.submit")}
                <ArrowUpRight
                  size={18}
                  className="group-hover:rotate-45 transition-transform duration-300"
                />
              </button>

              <p className="text-[12.5px] text-[#5C6660]/80 text-center leading-relaxed pt-2">
                {t("contacto.form.consent")}
              </p>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

const InfoRow = ({ icon, label, value, href, testid }) => {
  const body = (
    <>
      <div className="shrink-0 w-12 h-12 rounded-full bg-[#A3BD98]/15 flex items-center justify-center text-[#2A322C] mt-1 transition-colors group-hover:bg-[#A3BD98]/25">
        {icon}
      </div>
      <div>
        <div className="text-[10.5px] tracking-[0.28em] uppercase font-semibold text-[#5C6660] mb-1.5">
          {label}
        </div>
        <div className="text-[#2A322C] text-base md:text-[17px] font-medium">
          {value}
        </div>
      </div>
    </>
  );
  if (href)
    return (
      <a
        href={href}
        data-testid={testid}
        className="group flex items-start gap-5 hover:text-[#A3BD98] transition-colors"
      >
        {body}
      </a>
    );
  return (
    <div data-testid={testid} className="group flex items-start gap-5">
      {body}
    </div>
  );
};

const Field = ({ label, value, onChange, placeholder, type = "text", testid }) => (
  <div className="text-left">
    <label className="text-[11px] tracking-[0.26em] uppercase font-semibold text-[#ADBDAD] block mb-2.5">
      {label}
    </label>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-[#FEFEFE] border border-[#E4E9DE] rounded-[10px] px-4 h-[56px] text-[15px] text-[#2A322C] placeholder:text-[#5C6660]/60 focus:outline-none focus:border-[#A3BD98] focus:shadow-[0_0_0_4px_rgba(163,189,152,0.15)] hover:border-[#A3BD98]/60 transition-all duration-200"
    />
  </div>
);
